import dagre from '@dagrejs/dagre'

const SIZE_DIMENSIONS = {
  lg: { width: 400, height: 50 },
  md: { width: 200, height: 44 },
  sm: { width: 160, height: 40 }
}

function getNodeDimensions (node, defaultWidth, defaultHeight) {
  const size = node.data?.size
  if (size && SIZE_DIMENSIONS[size]) {
    return SIZE_DIMENSIONS[size]
  }
  return { width: defaultWidth, height: defaultHeight }
}

function layoutHierarchical (nodes, edges, options = {}) {
  const {
    direction = 'TB',
    nodeWidth = 220,
    nodeHeight = 50,
    nodeSep = 60,
    rankSep = 50
  } = options

  const g = new dagre.graphlib.Graph()
  g.setGraph({ rankdir: direction, nodesep: nodeSep, ranksep: rankSep })
  g.setDefaultEdgeLabel(() => ({}))

  nodes.forEach(n => {
    const dim = getNodeDimensions(n, nodeWidth, nodeHeight)
    g.setNode(n.id, { width: dim.width, height: dim.height })
  })
  edges.forEach(e => g.setEdge(e.source, e.target))

  dagre.layout(g)

  const positioned = nodes.map(n => {
    const pos = g.node(n.id)
    if (!pos) return n
    return {
      ...n,
      position: { x: pos.x - pos.width / 2, y: pos.y - pos.height / 2 }
    }
  })

  return centerSubtrees(positioned, edges)
}

function getSubtreeIds (nodeId, childrenOf) {
  const ids = []
  const queue = [nodeId]
  while (queue.length) {
    const id = queue.shift()
    const children = childrenOf.get(id) || []
    children.forEach(childId => {
      ids.push(childId)
      queue.push(childId)
    })
  }
  return ids
}

function getNodeCenterX (node) {
  const dim = getNodeDimensions(node, 200, 44)
  return node.position.x + dim.width / 2
}

function centerSubtrees (nodes, edges) {
  const nodeMap = new Map()
  nodes.forEach(n => nodeMap.set(n.id, n))

  const childrenOf = new Map()
  edges.forEach(e => {
    if (!childrenOf.has(e.source)) childrenOf.set(e.source, [])
    childrenOf.get(e.source).push(e.target)
  })

  const hasLevel = nodes.some(n => n.data?.levelIndex != null)
  if (!hasLevel) return nodes

  const maxLevel = nodes.reduce((max, n) => Math.max(max, n.data?.levelIndex ?? 0), 0)

  for (let level = maxLevel - 1; level >= 0; level--) {
    const parents = nodes.filter(n => (n.data?.levelIndex ?? -1) === level)

    parents.forEach(parent => {
      const directChildIds = childrenOf.get(parent.id) || []
      if (!directChildIds.length) return

      const parentCX = getNodeCenterX(parent)

      const childSubtrees = directChildIds.map(childId => {
        const child = nodeMap.get(childId)
        if (!child) return null
        const descIds = getSubtreeIds(childId, childrenOf)
        const allNodes = [child, ...descIds.map(id => nodeMap.get(id)).filter(Boolean)]

        let minX = Infinity
        let maxX = -Infinity
        allNodes.forEach(n => {
          const dim = getNodeDimensions(n, 200, 44)
          minX = Math.min(minX, n.position.x)
          maxX = Math.max(maxX, n.position.x + dim.width)
        })

        return { childId, allNodes, width: maxX - minX, centerX: (minX + maxX) / 2 }
      }).filter(Boolean)

      const totalWidth = childSubtrees.reduce((sum, st, i) => {
        return sum + st.width + (i > 0 ? 60 : 0)
      }, 0)

      let currentX = parentCX - totalWidth / 2

      childSubtrees.forEach((st, i) => {
        if (i > 0) currentX += 60
        const targetCenterX = currentX + st.width / 2
        const shiftX = targetCenterX - st.centerX

        st.allNodes.forEach(n => {
          n.position = { ...n.position, x: n.position.x + shiftX }
        })

        currentX += st.width
      })
    })
  }

  return nodes
}

function buildAdjacency (nodes, edges) {
  const adj = {}
  nodes.forEach(n => { adj[n.id] = [] })
  edges.forEach(e => {
    if (adj[e.source]) adj[e.source].push(e.target)
    if (adj[e.target]) adj[e.target].push(e.source)
  })
  return adj
}

function bfsDepths (rootId, adj) {
  const depths = {}
  const queue = [rootId]
  depths[rootId] = 0
  while (queue.length > 0) {
    const current = queue.shift()
    const neighbors = adj[current] || []
    for (const neighbor of neighbors) {
      if (depths[neighbor] === undefined) {
        depths[neighbor] = depths[current] + 1
        queue.push(neighbor)
      }
    }
  }
  return depths
}

function layoutRadial (nodes, edges, options = {}) {
  const { radius = 200, rootNodeId } = options

  if (nodes.length === 0) return nodes

  const adj = buildAdjacency(nodes, edges)
  const rootId = rootNodeId || nodes[0].id
  const depths = bfsDepths(rootId, adj)

  const rings = {}
  Object.entries(depths).forEach(([id, depth]) => {
    if (!rings[depth]) rings[depth] = []
    rings[depth].push(id)
  })

  const positionMap = {}
  positionMap[rootId] = { x: 0, y: 0 }

  Object.keys(rings).sort((a, b) => Number(a) - Number(b)).forEach(ring => {
    const ringNum = Number(ring)
    if (ringNum === 0) return
    const ids = rings[ring]
    const ringRadius = ringNum * radius
    ids.forEach((id, i) => {
      const angle = (2 * Math.PI * i) / ids.length
      positionMap[id] = {
        x: Math.cos(angle) * ringRadius,
        y: Math.sin(angle) * ringRadius
      }
    })
  })

  return nodes.map(n => ({
    ...n,
    position: positionMap[n.id] || { x: 0, y: 0 }
  }))
}

function layoutForce (nodes, edges, options = {}) {
  const {
    iterations: maxIterations = 100,
    repulsion = 5000,
    attraction = 0.01,
    damping = 0.9,
    velocityThreshold = 0.5
  } = options

  if (nodes.length === 0) return nodes

  const iterations = Math.min(maxIterations, Math.max(50, 300 - nodes.length))
  const nodeIdSet = new Set(nodes.map(n => n.id))
  const validEdges = edges.filter(e => nodeIdSet.has(e.source) && nodeIdSet.has(e.target))

  const positions = {}
  const velocities = {}

  nodes.forEach((n, i) => {
    const cols = Math.ceil(Math.sqrt(nodes.length))
    positions[n.id] = { x: (i % cols) * 200, y: Math.floor(i / cols) * 200 }
    velocities[n.id] = { x: 0, y: 0 }
  })

  for (let iter = 0; iter < iterations; iter++) {
    for (let i = 0; i < nodes.length; i++) {
      for (let j = i + 1; j < nodes.length; j++) {
        const a = nodes[i].id
        const b = nodes[j].id
        const dx = positions[b].x - positions[a].x
        const dy = positions[b].y - positions[a].y
        const dist = Math.max(Math.sqrt(dx * dx + dy * dy), 1)
        const force = repulsion / (dist * dist)
        const fx = (dx / dist) * force
        const fy = (dy / dist) * force

        velocities[a].x -= fx
        velocities[a].y -= fy
        velocities[b].x += fx
        velocities[b].y += fy
      }
    }

    validEdges.forEach(e => {
      const dx = positions[e.target].x - positions[e.source].x
      const dy = positions[e.target].y - positions[e.source].y
      const dist = Math.sqrt(dx * dx + dy * dy)
      const force = dist * attraction
      const fx = (dx / Math.max(dist, 1)) * force
      const fy = (dy / Math.max(dist, 1)) * force

      velocities[e.source].x += fx
      velocities[e.source].y += fy
      velocities[e.target].x -= fx
      velocities[e.target].y -= fy
    })

    let maxVelocity = 0
    nodes.forEach(n => {
      velocities[n.id].x *= damping
      velocities[n.id].y *= damping
      positions[n.id].x += velocities[n.id].x
      positions[n.id].y += velocities[n.id].y
      maxVelocity = Math.max(maxVelocity, Math.abs(velocities[n.id].x), Math.abs(velocities[n.id].y))
    })

    if (maxVelocity < velocityThreshold) break
  }

  return nodes.map(n => ({
    ...n,
    position: { x: Math.round(positions[n.id].x), y: Math.round(positions[n.id].y) }
  }))
}

function layoutFreeform (nodes) {
  const cols = Math.max(Math.ceil(Math.sqrt(nodes.length)), 1)
  return nodes.map((n, i) => {
    if (n.position && (n.position.x !== 0 || n.position.y !== 0)) return n
    return {
      ...n,
      position: {
        x: (i % cols) * 250,
        y: Math.floor(i / cols) * 150
      }
    }
  })
}

export function applyLayout (nodes, edges, layoutType = 'hierarchical', options = {}) {
  if (!nodes || nodes.length === 0) return nodes

  switch (layoutType) {
    case 'hierarchical':
      return layoutHierarchical(nodes, edges, options)
    case 'radial':
      return layoutRadial(nodes, edges, options)
    case 'force':
      return layoutForce(nodes, edges, options)
    case 'freeform':
      return layoutFreeform(nodes)
    default:
      return layoutHierarchical(nodes, edges, options)
  }
}
